/**
 * Recharge + Sticky ATC Price Display Fix
 * 
 * This script ensures that when switching between subscription and one-time purchase
 * in the Recharge widget, the sticky ATC bar price display is properly updated.
 */
(function() {
  // Ensure we only initialize once
  if (window.rechargeStickySyncInitialized) return;
  window.rechargeStickySyncInitialized = true;
  
  console.debug('[recharge-fix] Initializing Recharge + Sticky ATC price display fix');
  
  /**
   * Direct DOM manipulation to update price display
   * This is the most direct way to ensure the display updates correctly
   */
  function forceUpdatePriceDisplay() {
    // Get the sticky ATC bar
    const stickyATC = document.querySelector('sticky-atc-bar');
    if (!stickyATC) return;
    
    // Get key elements
    const sellingPlanInput = stickyATC.querySelector('[name="selling_plan"]');
    const priceWrapper = stickyATC.querySelector('.f-price');
    const salePriceElement = stickyATC.querySelector('.f-price-item--sale');
    const comparePriceElements = stickyATC.querySelectorAll('.f-price-item--regular');
    
    console.debug('[recharge-fix] Force updating price display', {
      sellingPlanValue: sellingPlanInput?.value || 'empty',
      priceWrapperClass: priceWrapper?.className,
      sellingPlanExists: !!sellingPlanInput
    });
    
    // Get current variant data
    const variantId = stickyATC.querySelector('[name="id"]')?.value;
    let currentVariant;
    
    // Multiple approaches to find variant data
    
    // 1. Try the global product JSON objects first
    if (window.ProductJSON && window.ProductJSON.variants) {
      currentVariant = window.ProductJSON.variants.find(v => String(v.id) === String(variantId));
    } else if (window.ReChargeProductJSON && window.ReChargeProductJSON.variants) {
      currentVariant = window.ReChargeProductJSON.variants.find(v => String(v.id) === String(variantId));
    }
    
    // 2. Try to get data from sticky ATC's current variant property if available
    if (!currentVariant && stickyATC.currentVariant) {
      currentVariant = stickyATC.currentVariant;
    }
    
    // 3. Look for product JSON data in script tags (common Shopify pattern)
    if (!currentVariant) {
      try {
        // Try to find the product JSON in script tags
        const productScripts = document.querySelectorAll('script[type="application/json"][id*="Product"]');
        for (const script of productScripts) {
          try {
            const data = JSON.parse(script.textContent);
            if (data && data.variants) {
              const variant = data.variants.find(v => String(v.id) === String(variantId));
              if (variant) {
                currentVariant = variant;
                // Store for future use
                window.ProductJSON = data;
                break;
              }
            }
          } catch (e) {
            console.debug('[recharge-fix] Error parsing product script:', e);
          }
        }
      } catch (e) {
        console.debug('[recharge-fix] Error searching product scripts:', e);
      }
    }
    
    // 4. If we still don't have variant data, try to extract it from the page
    if (!currentVariant) {
      // Don't show error during initial page load - this is expected
      const isInitialLoad = !window._rechargeFixInitialized;
      
      if (isInitialLoad) {
        console.debug('[recharge-fix] Variant data not yet available during initial load');
        // Mark as initialized for future calls
        window._rechargeFixInitialized = true;
      } else {
        console.warn('[recharge-fix] Could not find variant data, cannot update price');
      }
      
      return;
    }
    
    // Format settings
    const moneyFormat = window.theme?.moneyFormat || 
                        window.FoxTheme?.settings?.moneyFormat || 
                        "${{amount}}";
    
    // Format function
    const formatMoney = window.FoxTheme?.Currency?.formatMoney || 
                       (amount => {
                         return moneyFormat.replace('{{amount}}', (amount / 100).toFixed(2));
                       });
    
    // Step 1: Decide if on-sale class should be applied
    let shouldShowOnSale = false;
    let displayPrice = currentVariant.price;
    let compareAtPrice = currentVariant.compare_at_price;
    
    // If selling plan is selected
    if (sellingPlanInput && sellingPlanInput.value) {
      // Try to get selling plan price
      let sellingPlanPrice = null;
      
      if (window.variantSellingPlanAllocations && 
          window.variantSellingPlanAllocations[variantId]) {
        
        const allocations = window.variantSellingPlanAllocations[variantId];
        const sellingPlanId = sellingPlanInput.value;
        const matchingAllocation = allocations.find(a => 
          String(a.selling_plan_id) === String(sellingPlanId)
        );
        
        if (matchingAllocation && matchingAllocation.price) {
          sellingPlanPrice = matchingAllocation.price;
          compareAtPrice = matchingAllocation.compare_at_price || currentVariant.price;
          shouldShowOnSale = true;
          displayPrice = sellingPlanPrice;
          
          console.debug('[recharge-fix] Found selling plan price', {
            price: sellingPlanPrice,
            compareAtPrice: compareAtPrice
          });
        }
      }
    } 
    // If no selling plan, check for regular discount
    else if (currentVariant.compare_at_price && currentVariant.compare_at_price > currentVariant.price) {
      shouldShowOnSale = true;
    }
    
    // Step 2: Update the UI elements
    if (priceWrapper) {
      if (shouldShowOnSale) {
        priceWrapper.classList.add('f-price--on-sale');
        console.debug('[recharge-fix] Added on-sale class');
      } else {
        priceWrapper.classList.remove('f-price--on-sale');
        console.debug('[recharge-fix] Removed on-sale class');
      }
    }
    
    // Update the price display
    if (salePriceElement) {
      const formattedPrice = formatMoney(displayPrice, moneyFormat);
      salePriceElement.innerHTML = formattedPrice;
      console.debug('[recharge-fix] Updated price display:', formattedPrice);
    }
    
    // Update compare-at price if needed
    if (shouldShowOnSale && compareAtPrice && comparePriceElements && comparePriceElements.length) {
      const formattedComparePrice = formatMoney(compareAtPrice, moneyFormat);
      comparePriceElements.forEach(element => {
        element.innerHTML = formattedComparePrice;
      });
      console.debug('[recharge-fix] Updated compare price:', formattedComparePrice);
    }
  }
  
  /**
   * Monitor the document for selling plan changes
   */
  function monitorSellingPlanChanges() {
    // Track previous selling plan value to detect changes
    let prevSellingPlanValue = '';
    
    // Set up a MutationObserver to monitor the entire document
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        // Focus on attribute changes or node additions that might contain selling plan inputs
        if ((mutation.type === 'attributes' && 
             (mutation.target.name === 'selling_plan' || 
              mutation.target.getAttribute('data-selling-plan'))) || 
            mutation.type === 'childList') {
          
          // Check for selling plan inputs in the sticky ATC bar
          const stickyATC = document.querySelector('sticky-atc-bar');
          if (!stickyATC) continue;
          
          const sellingPlanInput = stickyATC.querySelector('[name="selling_plan"]');
          const currentValue = sellingPlanInput ? sellingPlanInput.value : '';
          
          // If value changed, update the price display
          if (currentValue !== prevSellingPlanValue) {
            console.debug('[recharge-fix] Selling plan changed', {
              from: prevSellingPlanValue || '(empty)',
              to: currentValue || '(empty)'
            });
            
            prevSellingPlanValue = currentValue;
            
            // Force update with a slight delay to ensure all DOM changes are complete
            setTimeout(forceUpdatePriceDisplay, 50);
          }
        }
      }
    });
    
    // Observe the entire document
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value', 'data-selling-plan', 'checked']
    });
    
    console.debug('[recharge-fix] Document observer for selling plan changes set up');
  }
  
  /**
   * Set up event listeners for all possible selling plan change events
   */
  function setupEventListeners() {
    // Listen for changes on selling plan inputs directly
    document.addEventListener('change', (e) => {
      // Check for elements that might be related to selling plans
      if (e.target.name === 'selling_plan' || 
          e.target.name === 'purchase_option' ||
          e.target.classList.contains('rc_widget__option__input') ||
          e.target.getAttribute('data-option-type') === 'one-time' ||
          e.target.getAttribute('data-option-type') === 'subscription') {
        
        console.debug('[recharge-fix] Selling plan related input changed', {
          element: e.target.tagName,
          name: e.target.name,
          value: e.target.value || e.target.checked
        });
        
        // Give time for values to propagate to the sticky ATC form
        setTimeout(forceUpdatePriceDisplay, 50);
      }
    }, true);
    
    // Listen for clicks on any elements that might trigger selling plan changes
    document.addEventListener('click', (e) => {
      // Find potential one-time or subscription buttons/labels
      const targetEl = e.target.closest('label, button, div.rc_widget__option, input[type="radio"]');
      if (!targetEl) return;
      
      // Check text content for relevant keywords
      const textContent = targetEl.textContent?.toLowerCase() || '';
      const isRelevant = textContent.includes('one time') || 
                        textContent.includes('one-time') || 
                        textContent.includes('subscription') ||
                        textContent.includes('deliver every') ||
                        textContent.includes('delivery');
      
      if (isRelevant || 
          targetEl.classList.contains('rc_widget__option') ||
          targetEl.getAttribute('data-option-type') === 'one-time' ||
          targetEl.getAttribute('data-option-type') === 'subscription') {
        
        console.debug('[recharge-fix] Potential selling plan trigger clicked', targetEl);
        setTimeout(forceUpdatePriceDisplay, 100);
      }
    }, true);
    
    // Add interval check for first few seconds
    let checkCount = 0;
    const checkInterval = setInterval(() => {
      checkCount++;
      
      // Get current selling plan value
      const stickyATC = document.querySelector('sticky-atc-bar');
      if (stickyATC) {
        const sellingPlanInput = stickyATC.querySelector('[name="selling_plan"]');
        // Only log if debug is enabled
        if (window.rechargeFixDebug) {
          console.debug('[recharge-fix] Interval check', {
            count: checkCount,
            sellingPlanValue: sellingPlanInput?.value || '(empty)'
          });
        }
        
        // After the first check, we're likely to have product data loaded
        if (checkCount > 1) {
          forceUpdatePriceDisplay();
        }
      }
      
      // Stop after 5 checks (5 seconds)
      if (checkCount >= 5) {
        clearInterval(checkInterval);
        // Final update with a little extra delay to ensure everything is loaded
        setTimeout(forceUpdatePriceDisplay, 300);
      }
    }, 1000);
  }
  
  // Initialize all monitoring systems
  monitorSellingPlanChanges();
  setupEventListeners();
  
  // Run initial update with sufficient delay to allow product data to load
  setTimeout(forceUpdatePriceDisplay, 1000);
  
  // Enable debug flag if needed
  // window.rechargeFixDebug = true;
  
  console.debug('[recharge-fix] Recharge + Sticky ATC price display fix initialized');
})();
